import React, { useContext, useEffect, useRef, useState } from "react";
import ScrollTrigger from "gsap/ScrollTrigger";
import PageTop from "../components/PageTop";
import { CursorContext } from "../context/CursorContext";
import BlogContent from "../components/BlogContent";

// 🧩 Blog posts data — replace with real posts
const blogs = [
  {
    title: "Building RAG Systems That Actually Understand Your Data",
    tag: "AI Engineering",
    date: "June 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995",
    excerpt:
      "Exploring Retrieval-Augmented Generation from architecture to implementation — covering embeddings, vector databases, chunking strategies, and how to build AI systems that answer with context instead of assumptions.",
    likes: 96,
  },
  {
    title: "AI Agents: Moving Beyond Simple Chatbots",
    tag: "Artificial Intelligence",
    date: "June 2026",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485",
    excerpt:
      "AI agents are changing how applications interact, reason, and execute tasks. A deep dive into agent workflows, tool calling, memory, and designing intelligent systems that solve real problems.",
    likes: 88,
  },
  {
    title: "From Natural Language to SQL: Teaching AI Databases",
    tag: "AI Engineering",
    date: "May 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
    excerpt:
      "Building an NL-to-SQL system requires more than converting text into queries. Exploring schema understanding, query validation, prompt strategies, and preventing AI hallucinations.",
    likes: 74,
  },
  {
    title: "Designing Scalable Microservices with Spring Boot",
    tag: "Backend",
    date: "April 2026",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31",
    excerpt:
      "Breaking applications into microservices introduces new challenges. Sharing lessons around service communication, API gateways, Docker containers, resilience patterns, and scalable backend design.",
    likes: 63,
  },
  {
    title: "Spring Boot: Beyond CRUD Applications",
    tag: "Java",
    date: "March 2026",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97",
    excerpt:
      "Modern Spring Boot applications are more than controllers and repositories. Exploring clean architecture, security, performance optimization, and building production-ready Java services.",
    likes: 81,
  },
  {
    title: "Building Modern Full-Stack Apps with React & Java",
    tag: "Full Stack",
    date: "March 2026",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
    excerpt:
      "A look into creating complete applications with React on the frontend and Spring Boot powering the backend — from authentication and APIs to deployment and user experience.",
    likes: 92,
  },
  {
    title: "System Design Lessons From Real Applications",
    tag: "Architecture",
    date: "February 2026",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31",
    excerpt:
      "Good software starts with good decisions. Exploring scalability, caching, databases, load balancing, and the architectural choices behind reliable systems.",
    likes: 69,
  },
  {
    title: "Dockerizing Applications: From Code to Containers",
    tag: "DevOps",
    date: "January 2026",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1605745341112-85968b19335b",
    excerpt:
      "Understanding how Docker simplifies development and deployment workflows by creating consistent environments across local machines, testing, and production systems.",
    likes: 57,
  },
  {
    title: "The Developer's Guide to Writing Better APIs",
    tag: "Backend",
    date: "December 2025",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c",
    excerpt:
      "APIs are the bridge between systems. Exploring REST principles, API security, documentation, versioning, and practices that make backend services easier to maintain.",
    likes: 52,
  },
  {
    title: "Aquascaping, Code, and the Art of Patience",
    tag: "Personal",
    date: "November 2025",
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1524704796725-9fc3044a58b2",
    excerpt:
      "Building software and maintaining a planted aquarium share the same philosophy — observe, adjust, and respect the small details that create something beautiful over time.",
    likes: 128,
  },
];
const Blog = () => {
  const scrollRef = useRef(null);
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    ScrollTrigger.defaults({ scroller: scrollRef.current });
    ScrollTrigger.refresh();
  }, []);

  // 🧩 Track which post is currently in view for the dot indicator
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const idx = Math.round(container.scrollTop / container.clientHeight);
      setActiveIndex(Math.min(idx, blogs.length - 1));
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // 🧩 Dot click scrolls to that post
  const scrollToPost = (i) => {
    scrollRef.current?.scrollTo({
      top: i * (scrollRef.current?.clientHeight ?? window.innerHeight),
      behavior: "smooth",
    });
  };

  // 🧩 Header is 100px mobile, 140px desktop. Footer is 52px.
  const HEADER_H =
    typeof window !== "undefined" && window.innerWidth >= 1024 ? 140 : 100;
  const FOOTER_H = 52;

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", paddingTop: HEADER_H }}
    >
      {/* ── Scroll container — fills space between header and footer ── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-scroll snap-y snap-mandatory scroll-smooth relative"
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        {blogs.map((blog, index) => (
          <BlogContent
            key={index}
            blog={blog}
            index={index}
            total={blogs.length}
          />
        ))}

        {/* ── Side dot navigator — inside scroll container so it stays in bounds ── */}
        <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2">
          {blogs.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToPost(i)}
              aria-label={`Go to post ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? "w-2 h-6 bg-violet-500"
                  : "w-2 h-2 bg-slate-300 dark:bg-slate-600 hover:bg-violet-400"
              }`}
            />
          ))}
        </div>

        <PageTop scrollContainerRef={scrollRef} />
      </div>

      {/* ── Footer — pinned below scroll area ── */}
      <div
        className="shrink-0 w-full text-center text-sm py-3 backdrop-blur-md bg-beige/60 dark:bg-black/40 border-t border-slate-200 dark:border-slate-700"
        style={{ height: FOOTER_H }}
      >
        Copyright ©️{new Date().getFullYear()} All rights reserved | This
        website is made by GuruLeo🍃
      </div>
    </div>
  );
};

export default Blog;
